<template>
    <BaseCard padding="lg" class="flex flex-col gap-4">
        <div>
            <h2 class="body-semibold text-ink">{{ t("reflections.sixty.card.collecting", { name: cycle.template.name }) }}</h2>
            <div class="mt-3">
                <div class="progress-track">
                    <div class="progress-fill" :style="{ width: `${progressPct}%` }"></div>
                </div>
                <div class="flex items-center justify-between mt-2">
                    <span class="details text-ink-muted">{{ responsesLabel }}</span>
                    <span v-if="cycle.closes_at" class="details text-ink-muted">{{ t("reflectionsAdmin.campaign.closes", { date: formatDate(cycle.closes_at, "short") }) }}</span>
                </div>
            </div>
        </div>

        <HorizontalDivider as="hr" spacing="none" />

        <div v-if="cycle.self_assess_enabled" class="self-assess-row">
            <div class="flex-1 min-w-0">
                <div class="body-semibold text-ink">{{ t("reflections.sixty.card.selfReflection") }}</div>
                <div v-if="selfReflectionIsTheBlocker" class="body-small text-ink">{{ ratersInCopy }}</div>
                <div v-else-if="!selfSubmitted" class="body-small text-ink-muted">
                    {{ t("reflections.sixty.card.selfPrompt") }}
                </div>
            </div>
            <StatusChip v-if="selfSubmitted" state="done" size="2xs" class="w-fit">{{ t("reflections.sixty.card.complete") }}</StatusChip>
            <BaseButton v-else type="button" :theme="selfReflectionIsTheBlocker ? 'primary' : 'neutral'" size="sm" class="whitespace-nowrap" @click="onOpenSelf">{{
                t("reflections.sixty.card.open")
            }}</BaseButton>
        </div>

        <HorizontalDivider v-if="cycle.self_assess_enabled" as="hr" spacing="none" />

        <ul class="flex flex-col divide-y divide-border -my-3">
            <li v-for="invite in visibleInvites" :key="invite.id" class="invite-row">
                <div class="invite-identity">
                    <PeopleAvatar size="sm" :name="invite.rater.display_name" />
                    <div class="flex-1 min-w-0 flex flex-col">
                        <div class="body-serif text-ink truncate">{{ invite.rater.display_name }}</div>
                        <div class="scaffold-details-small text-ink-muted truncate">{{ invite.rater.job_title || invite.rater.email }}</div>
                    </div>
                </div>
                <div class="invite-actions">
                    <BaseTooltip v-if="invite.invite_token" as-child theme="dark">
                        <template #trigger>
                            <BaseButton
                                type="button"
                                theme="tertiary"
                                size="sm"
                                icon-only
                                :aria-label="t('reflections.sixty.card.copyInviteLinkFor', { name: invite.rater.display_name })"
                                @click="onCopyLink(invite.invite_token, invite.id)">
                                <i class="bi" :class="copyStatus[invite.id] === 'copied' ? 'bi-check2' : 'bi-link-45deg'" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>{{ copyTooltip(invite.id) }}</template>
                    </BaseTooltip>
                    <BaseTooltip v-if="canActOnInvite(cycle, invite)" as-child theme="dark">
                        <template #trigger>
                            <BaseButton
                                type="button"
                                theme="tertiary"
                                size="sm"
                                icon-only
                                :aria-label="t('reflections.sixty.card.resendInviteTo', { name: invite.rater.display_name })"
                                :disabled="reminding[invite.id]"
                                @click="onRemind(invite.id)">
                                <i class="bi" :class="reminding[invite.id] ? 'bi-check2' : 'bi-send'" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>{{ reminding[invite.id] ? t("reflections.sixty.card.sent") : t("reflections.sixty.card.resendInvite") }}</template>
                    </BaseTooltip>
                    <StatusChip v-if="invite.state" :state="invite.state === 'responded' ? 'done' : 'pending'" size="2xs">
                        {{ invite.state === "responded" ? t("reflections.sixty.card.submitted") : t("reflections.sixty.card.pending") }}
                    </StatusChip>
                </div>
            </li>
        </ul>

        <HorizontalDivider as="hr" spacing="none" />

        <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-2">
                <a :href="`/reflections/360/${cycle.id}/nominate`" class="base-btn-neutral base-btn-sm whitespace-nowrap" @click.prevent="emit('invite-more', cycle.id)">
                    {{ t("reflections.sixty.card.inviteMore") }}
                </a>
                <BaseDropdownMenu :sections="menuSections" align="start" :side-offset="4" @select="handleMenuSelect">
                    <template #trigger>
                        <BaseButton theme="neutral" size="sm" icon-only :aria-label="t('reflections.sixty.card.moreActions')">
                            <i class="bi bi-three-dots" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                </BaseDropdownMenu>
            </div>
            <div class="flex flex-wrap items-center gap-2">
                <BaseTooltip v-if="canExtend" as-child theme="dark" content-class="max-w-72 text-left">
                    <template #trigger>
                        <BaseButton type="button" theme="neutral" size="sm" class="whitespace-nowrap" :disabled="extending || extendBlockedReason !== null" @click="onExtend">
                            {{ extending ? t("reflections.active360.extendButtonBusy") : t("reflections.active360.extendButton") }}
                        </BaseButton>
                    </template>
                    <template #content>{{ extendBlockedReason ?? t("reflections.active360.extendTooltip") }}</template>
                </BaseTooltip>
                <!-- A disabled button can't be hovered on touch or focused from the keyboard, so the reason is also plain text. -->
                <p v-if="canExtend && extendBlockedReason" class="body-small text-ink-muted basis-full">{{ extendBlockedReason }}</p>
                <BaseTooltip as-child theme="dark" content-class="max-w-72 text-left">
                    <template #trigger>
                        <BaseButton type="button" theme="primary" size="sm" class="whitespace-nowrap" :disabled="!canWrapUp" @click="onWrapUp">{{
                            t("reflections.sixty.card.wrapUp")
                        }}</BaseButton>
                    </template>
                    <template #content>{{ canWrapUp ? t("reflections.sixty.card.wrapUpTooltip") : wrapUpBlockedReason }}</template>
                </BaseTooltip>
            </div>
        </div>
        <BaseNotification v-if="bulkReminderStatus" :theme="bulkReminderStatus.theme" :text="bulkReminderStatus.text" @dismiss="bulkReminderStatus = null" />

        <ConfirmationDialog
            v-model:open="showBulkReminderConfirm"
            :title="t('reflections.sixty.card.bulkReminderTitle', { count: pendingCount })"
            :confirm-label="t('reflections.sixty.card.sendReminders')"
            :cancel-label="t('common.cancel')"
            :confirm-disabled="bulkReminderSending"
            :close-on-confirm="false"
            @confirm="onConfirmBulkRemind">
            <template #content>
                <I18nT keypath="reflections.sixty.card.bulkReminderBody" tag="div" class="body text-ink-muted">
                    <template #emphasis>
                        <strong class="text-ink">{{ t("reflections.sixty.card.bulkReminderBodyEmphasis") }}</strong>
                    </template>
                </I18nT>
            </template>
        </ConfirmationDialog>
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleForSubject, RemindersSentResponse } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { reportShortfallCopy, submittedProgressLabel, submittedProgressPct } from "~vue/utils/categoryThresholds";
import { canActOnInvite, formatDate } from "~vue/utils/threeSixty";
import { computed, onUnmounted, reactive, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

interface Props {
    cycle: CycleForSubject;
}

const { cycle } = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<{
    (event: "invite-more", cycleId: string): void;
    (event: "remind", inviteId: string): void;
    (event: "wrap-up", cycleId: string): void;
    (event: "open-self", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "extended", cycle: CycleForSubject): void;
}>();

// No "Delete 360" here: once launched the raters have been invited, so the
// subject can only add raters, remind them, or wrap up (see delete_cycle).

const copyStatus = reactive<Record<string, "copied" | "failed">>({});

function copyTooltip(inviteId: string): string {
    const status = copyStatus[inviteId];
    if (status === "copied") {
        return t("reflections.sixty.card.copied");
    }
    return status === "failed" ? t("reflections.sixty.card.copyFailed") : t("reflections.sixty.card.copyInviteLink");
}
const reminding = reactive<Record<string, boolean>>({});
// Track pending clear timers so we can cancel them on unmount and avoid
// touching state on a dead component (or growing the dicts unbounded across a
// long session).
const clearTimers = new Set<ReturnType<typeof setTimeout>>();
onUnmounted(() => {
    for (const id of clearTimers) {
        clearTimeout(id);
    }
    clearTimers.clear();
});

const selfSubmitted = computed(() => Boolean(cycle.self_response_submitted_at));

// Revoked/expired invites are technically still on the cycle but shouldn't appear
// in the subject-facing rater list. An organization that hides rater status sends
// no per-invite state, and has already dropped those invites server-side.
const visibleInvites = computed(() => (cycle.rater_status_hidden ? cycle.invites : cycle.invites.filter((i) => i.state === "pending" || i.state === "responded")));

const thresholds = computed(() => cycle.category_thresholds ?? null);

// The raters have already earned the report; only the subject's own reflection
// keeps it from including a self-vs-others comparison. `thresholds_met` rather
// than `eligible_for_report`, which also waits on the self reflection.
const ratersMetGate = computed(() => (thresholds.value !== null ? thresholds.value.thresholds_met : cycle.response_summary.responded >= cycle.min_responses));
const selfReflectionIsTheBlocker = computed(() => cycle.self_assess_enabled && !selfSubmitted.value && ratersMetGate.value);
const ratersInCopy = computed(() =>
    cycle.closes_at
        ? t("reflections.active360.selfReflectionRatersIn", { closesAt: formatDate(cycle.closes_at, "short") })
        : t("reflections.active360.selfReflectionRatersInNoDeadline"),
);

const responsesLabel = computed(() => {
    if (thresholds.value !== null) {
        return t("reflections.sixty.card.responsesWithProgress", { count: cycle.response_summary.responded, progress: submittedProgressLabel(thresholds.value) });
    }
    return t("reflections.sixty.card.responsesRecorded", { responded: cycle.response_summary.responded, total: Math.max(cycle.response_summary.total, cycle.min_responses) });
});

const progressPct = computed(() => {
    if (thresholds.value !== null) {
        return submittedProgressPct(thresholds.value);
    }
    const denom = Math.max(cycle.min_responses, cycle.response_summary.total);
    if (!denom) {
        return 0;
    }
    return Math.min(100, Math.round((cycle.response_summary.responded / denom) * 100));
});

const pendingCount = computed(() => (cycle.rater_status_hidden ? cycle.response_summary.pending : visibleInvites.value.filter((i) => i.state === "pending").length));
const hasPending = computed(() => pendingCount.value > 0);

const showBulkReminderConfirm = ref(false);
const bulkReminderSending = ref(false);
const bulkReminderStatus = ref<{ theme: "success" | "error" | "info"; text: string } | null>(null);

// Anonymity floor: manual wrap-up is only allowed once at least 3 raters have
// submitted feedback so no rater's response can be back-inferred. Independent
// of the admin-configured `min_responses` (which controls auto-unlock and can
// be set to any value ≥ 0).
const MIN_RESPONSES_TO_WRAP_UP = 3;
// Closing early can't hand over a report the thresholds would still withhold,
// so a threshold cycle has to clear them too.
// The self reflection is part of the gate on both paths: `eligible_for_report`
// already folds it in for thresholds, the count path checks it here.
const selfReflectionBlocksWrapUp = computed(() => cycle.self_assess_enabled && !selfSubmitted.value);
const canWrapUp = computed(
    () => cycle.response_summary.responded >= MIN_RESPONSES_TO_WRAP_UP && (thresholds.value === null ? !selfReflectionBlocksWrapUp.value : thresholds.value.eligible_for_report),
);

const wrapUpBlockedReason = computed(() => {
    const shortfall = thresholds.value === null ? null : reportShortfallCopy(thresholds.value);
    if (shortfall !== null) {
        return shortfall;
    }
    if (cycle.response_summary.responded >= MIN_RESPONSES_TO_WRAP_UP && selfReflectionBlocksWrapUp.value) {
        return t("reflections.sixty.card.wrapUpNeedsSelf");
    }
    return t("reflections.sixty.card.wrapUpNeedsResponses", { count: MIN_RESPONSES_TO_WRAP_UP });
});

function scheduleClear(fn: () => void, ms: number) {
    const id = setTimeout(() => {
        clearTimers.delete(id);
        fn();
    }, ms);
    clearTimers.add(id);
}

async function onCopyLink(inviteToken: string, inviteId: string) {
    const url = `${globalThis.location.origin}/reflections/360/r/${inviteToken}/`;
    try {
        await navigator.clipboard.writeText(url);
        copyStatus[inviteId] = "copied";
        scheduleClear(() => delete copyStatus[inviteId], 2000);
    } catch {
        copyStatus[inviteId] = "failed";
    }
}

function onRemind(inviteId: string) {
    reminding[inviteId] = true;
    emit("remind", inviteId);
    // Parent handles the fetch + toast; we reset the flag conservatively so
    // repeat clicks re-enable after a short guard window.
    scheduleClear(() => {
        reminding[inviteId] = false;
    }, 2000);
}

// One server-side call decides which raters are still pending, so the batch
// reminder works for an organization that hides individual rater status. It reports
// no counts back — how many were emailed is itself rater status.
async function onConfirmBulkRemind() {
    if (bulkReminderSending.value) {
        return;
    }
    bulkReminderSending.value = true;
    bulkReminderStatus.value = null;
    const { data, error } = await useFetch(`/v1/reflections/cycles/${cycle.id}/reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json<RemindersSentResponse>();
    bulkReminderSending.value = false;
    showBulkReminderConfirm.value = false;
    if (error.value || !data.value) {
        bulkReminderStatus.value = { theme: "error", text: t("reflections.sixty.card.remindersFailed") };
        return;
    }
    bulkReminderStatus.value = {
        theme: "success",
        text: t("reflections.sixty.card.remindersSent"),
    };
}

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: [
            {
                id: "send-batch-reminder",
                type: "button",
                label: bulkReminderSending.value ? t("reflections.sixty.card.sending") : t("reflections.sixty.card.sendBatchReminder"),
                disabled: !hasPending.value || bulkReminderSending.value,
            },
        ],
    },
]);

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "send-batch-reminder") {
        showBulkReminderConfirm.value = true;
    }
};

function onWrapUp() {
    emit("wrap-up", cycle.id);
}

// Only a self-launched 360 owns its deadline; a campaign cycle inherits the
// campaign's. The server enforces the same rules and a 60-day cap from launch.
const EXTENSION_DAYS = 14;
// Flipped when the server says the deadline lapsed between render and click.
const extendClosed = ref(false);
const canExtend = computed(
    () => !extendClosed.value && !cycle.is_campaign_cycle && cycle.state === "collecting" && Boolean(cycle.closes_at) && new Date(cycle.closes_at as string) > new Date(),
);
const extending = ref(false);
// Set from the server's 409; the data-derived check below covers the same state
// after a clamped extension or a reload, when the response has already landed.
const extendLimitReported = ref(false);
const atExtensionLimit = computed(() => Boolean(cycle.closes_at && cycle.extension_limit_at && cycle.closes_at >= cycle.extension_limit_at));
const extendBlockedReason = computed(() => (extendLimitReported.value || atExtensionLimit.value ? t("reflections.active360.extendLimitReached") : null));

async function onExtend() {
    if (extending.value) {
        return;
    }
    extending.value = true;
    const { data, error } = await useFetch(`/v1/reflections/cycles/${cycle.id}/extend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: EXTENSION_DAYS }),
    }).json<CycleEnvelopeResponse>();
    extending.value = false;
    if (error.value || !data.value?.cycle) {
        const code = (data.value as { code?: string } | null)?.code;
        if (code === "cycle_extension_limit_reached") {
            extendLimitReported.value = true;
            return;
        }
        if (code === "cycle_not_in_state") {
            extendClosed.value = true;
            bulkReminderStatus.value = { theme: "info", text: t("reflections.active360.extendClosed") };
            return;
        }
        bulkReminderStatus.value = { theme: "error", text: t("reflections.active360.extendFailed") };
        return;
    }
    emit("extended", data.value.cycle);
}

function onOpenSelf() {
    emit("open-self", { cycleId: cycle.id, title: cycle.template.name, closesAt: cycle.closes_at ?? null });
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .progress-track {
        @apply w-full h-2 bg-surface-sunken rounded-full overflow-hidden;
    }

    .progress-fill {
        @apply h-full bg-progress-fill rounded-full transition-[width];
    }

    /* Stack identity + actions on mobile so the name/role has full width; on sm+ the row
       is a single flex line matching the desktop layout. */
    .invite-row {
        @apply flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-3;
    }

    .invite-identity {
        @apply flex flex-1 min-w-0 items-center gap-3;
    }

    .invite-actions {
        /* On mobile the actions sit under the identity row, indented past the avatar
           (w-8 = 32px avatar + gap-3 = 12px) so they visually align with the name. */
        @apply flex items-center gap-2 pl-10 sm:pl-0;
    }

    .self-assess-row {
        @apply flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3;
    }
}
</style>
